import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';

import { apiClient } from '../lib/api.js';
import type { TalentSummary } from '@arx-marketplace/shared/talents';
import type { TulpaSummary } from '@arx-marketplace/shared/tulpas';
import { cn, formatVersion } from '../lib/utils.js';

type Tab = 'all' | 'talents' | 'tulpas';
type Category = 'skill' | 'knowledge' | 'toolchain' | 'policy';

const TABS: { value: Tab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'talents', label: 'Talents' },
  { value: 'tulpas', label: 'Tulpas' },
];

const CATEGORIES: { value: Category | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'skill', label: 'Skills' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'toolchain', label: 'Toolchains' },
  { value: 'policy', label: 'Policies' },
];

export function HomePage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('all');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<Category | ''>('');
  const [talents, setTalents] = useState<TalentSummary[]>([]);
  const [tulpas, setTulpas] = useState<TulpaSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    const wantTalents = tab === 'all' || tab === 'talents';
    const wantTulpas = tab === 'all' || tab === 'tulpas';

    Promise.all([
      wantTalents
        ? apiClient.listTalents({ q: q || undefined, category: category || undefined, per_page: 50 })
        : Promise.resolve({ items: [] as TalentSummary[], page: 1, per_page: 0, total: 0 }),
      wantTulpas
        ? apiClient.listTulpas({ q: q || undefined, per_page: 50 })
        : Promise.resolve({ items: [] as TulpaSummary[], page: 1, per_page: 0, total: 0 }),
    ])
      .then(([tl, tp]) => {
        if (!alive) return;
        setTalents(tl.items);
        setTulpas(tp.items);
      })
      .catch((e: Error) => alive && setErr(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tab, q, category]);

  const empty = !loading && !err && talents.length === 0 && tulpas.length === 0;

  return (
    <section className="container py-10 flex-1">
      <h1 className="text-3xl font-bold tracking-tight">Browse the marketplace</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        <strong>Talents</strong> are single capabilities (skill / knowledge / toolchain / policy).{' '}
        <strong>Tulpas</strong> are complete agents — a persona plus the talents it depends on —
        shipped as portable <code>.tulpa</code> bundles.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'px-4 py-2 text-sm -mb-px border-b-2 transition-colors',
              tab === t.value
                ? 'border-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + (talent) category filter */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className={cn(
              'w-full pl-9 pr-3 py-2 rounded-md border bg-background',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          />
        </div>
        {tab !== 'tulpas' && (
          <div className="flex gap-1 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm border transition-colors',
                  category === c.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'hover:bg-secondary',
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {err && <div className="mt-6 text-destructive text-sm">{err}</div>}
      {loading && <div className="mt-6 text-muted-foreground text-sm">Loading…</div>}

      {/* Tulpas */}
      {tulpas.length > 0 && (
        <>
          {tab === 'all' && <h2 className="mt-8 text-xl font-semibold">Tulpas</h2>}
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tulpas.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/u/${t.slug}`}
                  className="block rounded-lg border p-4 hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold truncate">{t.name}</h3>
                    {t.latest_version && (
                      <code className="text-xs text-muted-foreground">{formatVersion(t.latest_version).label}</code>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {t.summary || 'No summary yet.'}
                  </p>
                  <div className="mt-3 flex items-center gap-1.5 flex-wrap text-xs text-muted-foreground">
                    {t.engines.map((e) => (
                      <span key={e} className="px-1.5 py-0.5 rounded bg-muted">
                        {e}
                      </span>
                    ))}
                    <span>· {t.talent_count} talents</span>
                    {t.owner_name && <span>· by {t.owner_name}</span>}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Talents */}
      {talents.length > 0 && (
        <>
          {tab === 'all' && <h2 className="mt-8 text-xl font-semibold">Talents</h2>}
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {talents.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/t/${t.slug}`}
                  className="block rounded-lg border p-4 hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold truncate">{t.name}</h3>
                    {t.latest_version && (
                      <code className="text-xs text-muted-foreground">{formatVersion(t.latest_version).label}</code>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {t.summary || 'No summary yet.'}
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="px-1.5 py-0.5 rounded bg-muted">{t.category}</span>
                    {t.owner_name && <span>· by {t.owner_name}</span>}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {empty && <div className="mt-8 text-muted-foreground text-sm">Nothing matches your filter.</div>}
    </section>
  );
}
